import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { createEditor, Range, Editor, Transforms, Element as SlateElement } from 'slate';
import { Slate, Editable, withReact, useSlate, useSlateStatic, useFocused } from 'slate-react';
import { withHistory } from 'slate-history';
import {
  Save,
  Plus,
  FileText,
  Image,
  BarChart3,
  Table,
  Columns,
  Layout,
  Minus,
  Eye,
  Edit3,
  Languages,
  Loader2,
  AlertCircle,
  CheckCircle,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Type,
  Palette,
  ChevronDown,
  X,
  Undo,
  Redo,
  Quote,
  Code,
} from 'lucide-react';
import { SketchPicker } from 'react-color';

// Import config and elements
import { 
  CUSTOM_ELEMENT_TYPES, 
  convertBlocksToPlateValue, 
  convertPlateValueToBlocks,
} from './PlateConfig';
import {
  ChartElement,
  HeroElement,
  DataTableElement,
  DividerElement,
  StaticImageElement,
  ColumnGroupElement,
  ColumnElement,
} from './elements';
import translations from '../translations';

// Import assets
import CloseIcon from '../../../../assets/images/close.png';
import spinner from '../../../../assets/images/loader_blue.svg';

// Styles
import '../ReportEditor.css';

// ============================================
// SLATE HELPERS
// ============================================

const LIST_TYPES = ['ul', 'ol'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  );

  return !!match;
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });

  let newProperties;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? 'p' : isList ? 'li' : format,
    };
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

// ============================================
// TOOLBAR BUTTON COMPONENTS
// ============================================

const Button = React.forwardRef(({ className, active, reversed, ...props }, ref) => (
  <span
    {...props}
    ref={ref}
    className={`cursor-pointer p-2 rounded-md transition-all duration-150 inline-flex items-center justify-center
      ${reversed
        ? active
          ? 'text-white bg-gray-700'
          : 'text-gray-400 hover:text-gray-200'
        : active
          ? 'text-blue-600 bg-blue-50'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }
      ${className || ''}`}
  />
));

const Icon = ({ className, ...props }) => (
  <span
    {...props}
    className={`text-lg align-text-bottom ${className || ''}`}
  />
);

const Menu = React.forwardRef(({ className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={`flex items-center gap-1 ${className || ''}`}
  />
));

// Mark Button
const MarkButton = ({ format, icon: IconComponent }) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={event => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      title={format.charAt(0).toUpperCase() + format.slice(1)}
    >
      <IconComponent size={18} />
    </Button>
  );
};

// Block Button
const BlockButton = ({ format, icon: IconComponent }) => {
  const editor = useSlate();
  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
      )}
      onMouseDown={event => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      title={format.charAt(0).toUpperCase() + format.slice(1)}
    >
      <IconComponent size={18} />
    </Button>
  );
};

// ============================================
// COLOR PICKER COMPONENT
// ============================================

const ColorButton = ({ format, icon: IconComponent, title, defaultColor }) => {
  const editor = useSlate();
  const [showPicker, setShowPicker] = useState(false);
  const [color, setColor] = useState(defaultColor);
  const buttonRef = useRef(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleColorChange = (newColor) => {
    setColor(newColor.hex);
    Editor.addMark(editor, format, newColor.hex);
  };

  const marks = Editor.marks(editor);
  const currentColor = marks?.[format] || color;

  return (
    <div className="relative inline-block">
      <div
        ref={buttonRef}
        className="cursor-pointer p-2 rounded-md hover:bg-gray-100 inline-flex items-center gap-1"
        onMouseDown={(e) => {
          e.preventDefault();
          setShowPicker(!showPicker);
        }}
        title={title}
      >
        <IconComponent size={18} />
        <div 
          className="w-4 h-1.5 rounded-sm border border-gray-300" 
          style={{ backgroundColor: currentColor }}
        />
        <ChevronDown size={12} />
      </div>
      
      {showPicker && (
        <div 
          ref={pickerRef}
          className="absolute top-full left-0 mt-2 z-[9999] bg-white rounded-lg shadow-2xl border"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between p-2 border-b bg-gray-50">
            <span className="text-sm font-medium">{title}</span>
            <button 
              onClick={() => setShowPicker(false)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <X size={14} />
            </button>
          </div>
          <SketchPicker
            color={currentColor}
            onChange={handleColorChange}
            disableAlpha
            presetColors={[
              '#bb133e', '#e74c3c', '#e67e22', '#f1c40f', '#2ecc71',
              '#1abc9c', '#3498db', '#9b59b6', '#34495e', '#000000',
              '#7f8c8d', '#95a5a6', '#bdc3c7', '#ecf0f1', '#ffffff',
            ]}
          />
        </div>
      )}
    </div>
  );
};

// ============================================
// FONT SIZE SELECTOR
// ============================================

const FontSizeSelector = () => {
  const editor = useSlate();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const sizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];
  
  const marks = Editor.marks(editor);
  const currentSize = marks?.fontSize || '16px';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-gray-100 text-sm min-w-[70px] justify-between border border-gray-200"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        <span>{currentSize}</span>
        <ChevronDown size={14} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border z-[9999] min-w-[80px] max-h-[200px] overflow-y-auto">
          {sizes.map((size) => (
            <button
              key={size}
              className={`w-full text-left px-3 py-1.5 hover:bg-gray-100 text-sm ${
                currentSize === size ? 'bg-blue-50 text-blue-600' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                Editor.addMark(editor, 'fontSize', size);
                setIsOpen(false);
              }}
            >
              {size}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// HEADING SELECTOR
// ============================================

const HeadingSelector = () => {
  const editor = useSlate();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const headings = [
    { type: 'p', label: 'Paragraph', style: 'text-sm' },
    { type: 'h1', label: 'Heading 1', style: 'text-xl font-bold' },
    { type: 'h2', label: 'Heading 2', style: 'text-lg font-bold' },
    { type: 'h3', label: 'Heading 3', style: 'text-base font-semibold' },
  ];

  const getCurrentHeading = () => {
    for (const heading of headings) {
      if (isBlockActive(editor, heading.type)) {
        return heading;
      }
    }
    return headings[0];
  };

  const current = getCurrentHeading();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-gray-100 text-sm min-w-[120px] justify-between border border-gray-200"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        <span className={current.style}>{current.label}</span>
        <ChevronDown size={14} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border z-[9999] min-w-[150px]">
          {headings.map((heading) => (
            <button
              key={heading.type}
              className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${heading.style} ${
                current.type === heading.type ? 'bg-blue-50' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleBlock(editor, heading.type);
                setIsOpen(false);
              }}
            >
              {heading.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// HOVERING TOOLBAR (appears on selection)
// ============================================

const HoveringToolbar = () => {
  const ref = useRef(null);
  const editor = useSlate();
  const inFocus = useFocused();

  useEffect(() => {
    const el = ref.current;
    const { selection } = editor;

    if (!el) return;

    if (
      !selection ||
      !inFocus ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ''
    ) {
      el.removeAttribute('style');
      return;
    }

    const domSelection = window.getSelection();
    if (!domSelection || domSelection.rangeCount === 0) return;
    
    const domRange = domSelection.getRangeAt(0);
    const rect = domRange.getBoundingClientRect();
    
    el.style.opacity = '1';
    el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight - 10}px`;
    el.style.left = `${rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2}px`;
  });

  return (
    <div
      ref={ref}
      className="absolute z-[9999] -top-[10000px] -left-[10000px] opacity-0 transition-opacity duration-150 bg-gray-800 rounded-lg shadow-2xl p-1 flex items-center gap-0.5"
      onMouseDown={e => e.preventDefault()}
    >
      <MarkButton format="bold" icon={Bold} />
      <MarkButton format="italic" icon={Italic} />
      <MarkButton format="underline" icon={Underline} />
      <MarkButton format="strikethrough" icon={Strikethrough} />
      <div className="w-px h-5 bg-gray-600 mx-1" />
      <ColorButton format="color" icon={Type} title="Text Color" defaultColor="#000000" />
      <ColorButton format="backgroundColor" icon={Palette} title="Highlight" defaultColor="#FFFF00" />
    </div>
  );
};

// ============================================
// MAIN TOOLBAR
// ============================================

const Toolbar = ({ children }) => (
  <div className="flex items-center gap-1 flex-wrap p-2 bg-white border-b">
    {children}
  </div>
);

const ToolbarDivider = () => (
  <div className="w-px h-6 bg-gray-300 mx-1" />
);

// ============================================
// ELEMENT RENDERER
// ============================================

const createRenderElement = ({ chartData, reportData, onUpdateElement, onDeleteElement, language }) => {
  return (props) => {
    const { element, attributes, children } = props;

    const style = { textAlign: element.align };

    switch (element.type) {
      case CUSTOM_ELEMENT_TYPES.CHART:
        return (
          <ChartElement
            {...props}
            chartData={chartData}
            onUpdate={onUpdateElement}
            onDelete={onDeleteElement}
          />
        );

      case CUSTOM_ELEMENT_TYPES.HERO:
        return (
          <HeroElement
            {...props}
            onUpdate={onUpdateElement}
            onDelete={onDeleteElement}
          />
        );

      case CUSTOM_ELEMENT_TYPES.DATA_TABLE:
        return (
          <DataTableElement
            {...props}
            reportData={reportData}
            onUpdate={onUpdateElement}
            onDelete={onDeleteElement}
            language={language}
          />
        );

      case CUSTOM_ELEMENT_TYPES.DIVIDER:
        return (
          <DividerElement
            {...props}
            onUpdate={onUpdateElement}
            onDelete={onDeleteElement}
          />
        );

      case CUSTOM_ELEMENT_TYPES.STATIC_IMAGE:
        return (
          <StaticImageElement
            {...props}
            onUpdate={onUpdateElement}
            onDelete={onDeleteElement}
          />
        );

      case 'column_group':
        return (
          <ColumnGroupElement
            {...props}
            chartData={chartData}
            reportData={reportData}
            onUpdate={onUpdateElement}
            onDelete={onDeleteElement}
          />
        );

      case 'column':
        return (
          <ColumnElement
            {...props}
            onUpdate={onUpdateElement}
          />
        );

      case 'blockquote':
        return (
          <blockquote 
            {...attributes} 
            className="border-l-4 border-gray-300 pl-4 my-4 italic text-gray-600"
            style={style}
          >
            {children}
          </blockquote>
        );

      case 'code':
        return (
          <pre {...attributes} className="bg-gray-100 p-4 rounded-lg my-4 font-mono text-sm overflow-x-auto">
            <code>{children}</code>
          </pre>
        );

      case 'h1':
        return (
          <h1 
            {...attributes} 
            className="text-4xl font-bold mt-6 mb-2 text-gray-900"
            style={style}
          >
            {children}
          </h1>
        );

      case 'h2':
        return (
          <h2 
            {...attributes} 
            className="text-3xl font-bold mt-5 mb-2 text-gray-800"
            style={style}
          >
            {children}
          </h2>
        );

      case 'h3':
        return (
          <h3 
            {...attributes} 
            className="text-2xl font-semibold mt-4 mb-1 text-gray-800"
            style={style}
          >
            {children}
          </h3>
        );

      case 'ul':
        // Check if any children have circleType (custom colored bullets)
        const hasCustomBullets = element.children?.some(child => child.circleType);
        return (
          <ul 
            {...attributes} 
            className={hasCustomBullets ? "my-2" : "list-disc ml-6 my-2 space-y-1"} 
            style={style}
          >
            {children}
          </ul>
        );

      case 'ol':
        return (
          <ol {...attributes} className="list-decimal ml-6 my-2 space-y-1" style={style}>
            {children}
          </ol>
        );

      case 'li':
        // Color mapping for risk appetite circles
        const circleColors = {
          averse: '#336699',      // Blue
          minimalist: '#66cccc',  // Teal
          cautious: '#fccb99',    // Light orange/peach
          open: '#cc6666',        // Salmon/pink
          hungry: '#991433',      // Dark red
        };
        const circleType = element.circleType;
        const circleColor = circleType ? circleColors[circleType] : null;
        
        if (circleColor) {
          // Custom colored circle list item
          return (
            <li {...attributes} className="text-gray-700 flex items-start gap-3 mb-4 list-none">
              <span 
                contentEditable={false}
                className="flex-shrink-0 w-4 h-4 rounded-full mt-1"
                style={{ backgroundColor: circleColor }}
              />
              <span className="flex-1">{children}</span>
            </li>
          );
        }
        
        // Normal list item with standard bullet
        return (
          <li {...attributes} className="text-gray-700 mb-2">
            {children}
          </li>
        );

      case 'p':
      default:
        // Handle styled paragraphs (like red banner, conclusion box)
        const pStyle = { ...style };
        if (element.backgroundColor) pStyle.backgroundColor = element.backgroundColor;
        if (element.textAlign) pStyle.textAlign = element.textAlign;
        if (element.padding) pStyle.padding = element.padding;
        if (element.paddingTop) pStyle.paddingTop = element.paddingTop;
        if (element.border) pStyle.border = element.border;
        if (element.borderTop) pStyle.borderTop = element.borderTop;
        if (element.marginTop) pStyle.marginTop = element.marginTop;
        if (element.marginBottom) pStyle.marginBottom = element.marginBottom;
        
        const pClassName = element.backgroundColor 
          ? "leading-relaxed" 
          : element.border 
            ? "text-gray-700 leading-relaxed"
            : "mb-2 text-gray-700 leading-relaxed";
        
        return (
          <p 
            {...attributes} 
            className={pClassName}
            style={pStyle}
          >
            {children}
          </p>
        );
    }
  };
};

// ============================================
// LEAF RENDERER
// ============================================

const Leaf = ({ attributes, children, leaf }) => {
  const style = {};
  
  if (leaf.color) style.color = leaf.color;
  if (leaf.backgroundColor) style.backgroundColor = leaf.backgroundColor;
  if (leaf.fontSize) style.fontSize = leaf.fontSize;

  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  if (leaf.strikethrough) {
    children = <s>{children}</s>;
  }
  if (leaf.code) {
    children = <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-sm">{children}</code>;
  }

  return (
    <span {...attributes} style={Object.keys(style).length ? style : undefined}>
      {children}
    </span>
  );
};

// ============================================
// HTML TO SLATE NODES HELPER
// ============================================

function htmlToSlateNodes(html) {
  if (!html) return [{ type: 'p', children: [{ text: '' }] }];
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  
  const nodes = [];
  
  const processNode = (node, parentTag = null) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!text.trim()) return null;
      return { text };
    }
    
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    
    const tagName = node.tagName.toLowerCase();
    const style = node.getAttribute('style') || '';
    
    // Extract marks from style
    const marks = {};
    if (style.includes('font-weight: bold') || style.includes('font-weight:bold')) marks.bold = true;
    
    const children = Array.from(node.childNodes)
      .map(child => processNode(child, tagName))
      .filter(Boolean)
      .flat();
    
    if (children.length === 0) children.push({ text: '' });
    
    switch (tagName) {
      case 'p':
        return { type: 'p', children };
      case 'ul':
        const ulChildren = children.filter(c => c.type === 'li');
        return { type: 'ul', children: ulChildren.length > 0 ? ulChildren : [{ type: 'li', children: [{ text: '' }] }] };
      case 'ol':
        const olChildren = children.filter(c => c.type === 'li');
        return { type: 'ol', children: olChildren.length > 0 ? olChildren : [{ type: 'li', children: [{ text: '' }] }] };
      case 'li':
        // Extract text content, flattening nested paragraphs
        const liContent = [];
        children.forEach(child => {
          if (child.text !== undefined) {
            liContent.push(child);
          } else if (child.type === 'p' && child.children) {
            child.children.forEach(c => {
              if (c.text !== undefined) liContent.push(c);
            });
          }
        });
        return { type: 'li', children: liContent.length > 0 ? liContent : [{ text: '' }] };
      case 'strong':
      case 'b':
        return children.map(child => {
          if (child.text !== undefined) return { ...child, bold: true };
          return child;
        });
      case 'em':
      case 'i':
        return children.map(child => {
          if (child.text !== undefined) return { ...child, italic: true };
          return child;
        });
      case 'span':
      case 'div':
        return children;
      default:
        return children;
    }
  };
  
  body.childNodes.forEach(node => {
    const result = processNode(node);
    if (result) {
      if (Array.isArray(result)) {
        nodes.push(...result);
      } else {
        nodes.push(result);
      }
    }
  });
  
  return nodes.length > 0 ? nodes : [{ type: 'p', children: [{ text: '' }] }];
}

// ============================================
// DEFAULT TEMPLATE
// ============================================

function createDefaultTemplate(t, language = 'tr') {
  const ts = Date.now();
  const isEnglish = language === 'en';

  // Inline translations for hardcoded content
  const txt = {
    // Risk Appetite items
    averse: isEnglish ? 'Averse: No Risk Appetite' : 'Kaçınan: Risk İştahı Yok',
    averseDesc: isEnglish ? ' – Prioritizes safety and stability, actively avoids risks, and prefers highly controlled environments.' : ' – Güvenlik ve istikrarı önceliklendirir, risklerden aktif olarak kaçınır ve yüksek kontrollü ortamları tercih eder.',
    minimalist: isEnglish ? 'Minimalist: Very Low Risk Appetite' : 'Minimalist: Çok Düşük Risk İştahı',
    minimalistDesc: isEnglish ? ' – Focuses only on essential, low-impact risks when necessary, maintaining a lean approach to risk-taking.' : ' – Sadece gerekli olduğunda, temel, düşük etkili risklere odaklanır, risk almada yalın bir yaklaşım sürdürür.',
    cautious: isEnglish ? 'Cautious: Low Risk Appetite' : 'Temkinli: Düşük Risk İştahı',
    cautiousDesc: isEnglish ? ' – Takes calculated risks with significant preventive measures, preferring known outcomes with minimal uncertainty.' : ' – Önemli önleyici tedbirlerle hesaplanmış riskler alır, minimum belirsizlikle bilinen sonuçları tercih eder.',
    open: isEnglish ? 'Open: Medium Risk Appetite' : 'Açık: Orta Risk İştahı',
    openDesc: isEnglish ? ' – Willing to take some risks while balancing potential gains and losses, evaluating innovative opportunities.' : ' – Potansiyel kazanç ve kayıpları dengelerken yenilikçi fırsatları değerlendirerek bazı riskleri almaya isteklidir.',
    hungry: isEnglish ? 'Hungry: High Risk Appetite' : 'Hırslı: Yüksek Risk İştahı',
    hungryDesc: isEnglish ? ' – Actively seeks risk for potential high rewards, tolerating significant uncertainty and variability in outcomes.' : ' – Yüksek ödüller potansiyeli için aktif olarak risk arar, sonuçlarda önemli belirsizlik ve değişkenliği tolere eder.',
    
    // Risk Categories
    operationalRisks: isEnglish ? 'Operational Risks: ' : 'Operasyonel Riskler: ',
    operationalRisksDesc: isEnglish ? 'Potential business operations disruption from facility closures, suspensions, prohibitions, and loss of license.' : 'Tesis kapanmaları, askıya almalar, yasaklamalar ve lisans kaybından kaynaklanan potansiyel iş operasyonları kesintisi.',
    financialRisks: isEnglish ? 'Financial Risks: ' : 'Finansal Riskler: ',
    financialRisksDesc: isEnglish ? 'Internal and external costs related to investigation and remediation (e.g., legal fees, consultants, investigators).' : 'Soruşturma ve iyileştirme ile ilgili iç ve dış maliyetler (örn. yasal ücretler, danışmanlar, araştırmacılar).',
    strategicRisks: isEnglish ? 'Strategic Risks: ' : 'Stratejik Riskler: ',
    strategicRisksDesc: isEnglish ? 'Prohibition of adding new customers, loss of license.' : 'Yeni müşteri ekleme yasağı, lisans kaybı.',
    complianceRisks: isEnglish ? 'Compliance Risks: ' : 'Uyum Riskleri: ',
    complianceRisksDesc: isEnglish ? 'Regulatory compliance risks and reporting new requirements.' : 'Düzenleyici uyum riskleri ve yeni gereksinimleri raporlama.',
    reputationalRisks: isEnglish ? 'Reputational Risks: ' : 'İtibar Riskleri: ',
    reputationalRisksDesc: isEnglish ? 'Impact of media coverage; damage to organization\'s image/brand; and consequently reduced attractiveness to current and potential future employees, business partners, suppliers, and customers.' : 'Medya kapsamının etkisi; kuruluşun imajına/markasına zarar; ve sonuç olarak mevcut ve potansiyel gelecekteki çalışanlar, iş ortakları, tedarikçiler ve müşteriler için azalan çekicilik.',
    
    // Section 4 - Risk Types
    inherentRisk: isEnglish ? 'Inherent Risk: ' : 'Doğal Risk: ',
    inherentRiskDesc: isEnglish ? 'The level of risk that exists before any control or mitigating action is applied. Represents the raw, unmodified exposure to a specific threat or vulnerability.' : 'Herhangi bir kontrol veya azaltıcı eylem uygulanmadan önce var olan risk seviyesi. Belirli bir tehdit veya güvenlik açığına ham, değiştirilmemiş maruz kalma seviyesini temsil eder.',
    residualRisk: isEnglish ? 'Residual Risk: ' : 'Artık Risk: ',
    residualRiskDesc: isEnglish ? 'The level of risk remaining after controls and mitigating actions have been applied. Reflects the organization\'s exposure after measures have been taken to reduce or manage inherent risks.' : 'Kontroller ve azaltıcı eylemler uygulandıktan sonra kalan risk seviyesi. Doğal riskleri azaltmak veya yönetmek için önlemler alındıktan sonra kuruluşun maruziyetini yansıtır.',
    futureRisk: isEnglish ? 'Future Risk: ' : 'Gelecek Riski: ',
    futureRiskDesc: isEnglish ? 'Future Risk or Target Risk Level refers to the level of risk an organization aims to achieve or maintain in the future.' : 'Gelecek Risk veya Hedef Risk Seviyesi, bir kuruluşun gelecekte gerçekleştirmeyi veya sürdürmeyi amaçladığı risk seviyesini ifade eder.',
    keyDistinction: isEnglish ? 'Key Distinction' : 'Temel Ayrım',
    inherentShort: isEnglish ? 'Initial state of risk, before mitigation.' : 'Riskin ilk durumu, azaltma öncesi.',
    residualShort: isEnglish ? 'After mitigation, remaining risk.' : 'Azaltma sonrası, kalan risk.',
    futureShort: isEnglish ? 'Accepted risk level.' : 'Kabul edilen risk seviyesi.',
    likelihood: isEnglish ? 'Likelihood of occurrence' : 'Gerçekleşme olasılığı',
    likelihoodDesc: isEnglish ? ', refers to the probability or chance that a specific risk event will happen.' : ', belirli bir risk olayının gerçekleşme ihtimali veya şansını ifade eder.',
    
    // Section 5 - Risk Matrix
    purpose: isEnglish ? 'Purpose: ' : 'Amaç: ',
    purposeDesc: isEnglish ? 'Visualize and categorize risks to prioritize which ones require more attention.' : 'Hangi risklerin daha fazla dikkat gerektirdiğini önceliklendirmek için riskleri görselleştirme ve kategorize etme.',
    matrixDesign: isEnglish ? 'Matrix Design: ' : 'Matris Tasarımı: ',
    matrixDesignDesc: isEnglish ? 'A typical matrix includes "Likelihood" on one axis and "Impact" on the other, with resulting cells indicating low, medium, or high overall risk.' : 'Tipik bir matris, bir eksende "Olasılık" ve diğerinde "Etki" içerir, sonuçta oluşan hücreler düşük, orta veya yüksek genel riski gösterir.',
    prioritization: isEnglish ? 'Prioritization: ' : 'Önceliklendirme: ',
    prioritizationDesc: isEnglish ? 'Risks in the high-likelihood, high-impact quadrant are top priorities, while low-likelihood, low-impact risks may require minimal action.' : 'Yüksek olasılık, yüksek etki çeyreğindeki riskler en öncelikli iken, düşük olasılık, düşük etki riskleri minimal eylem gerektirebilir.',
    
    // Section 6 - Department overview
    riskDistribution: isEnglish ? 'Risk Distribution by Department' : 'Departmanlara Göre Risklerin Dağılımı',
    riskDistributionDesc: isEnglish ? 'As shown above, our company currently manages a total of 6 identified risks, distributed across key departments. The department with the highest share is IT, followed by Finance at 33%.' : 'Yukarıda görüldüğü üzere, şirketimiz şu anda toplam 6 tanımlanmış riski yönetmektedir ve bu riskler kilit departmanlara dağılmış durumdadır. En yüksek orana sahip olan departman IT, onu %33 oranıyla Finans departmanı takip etmektedir.',
    residualByDept: isEnglish ? 'Residual Risk Level by Department' : 'Departmanlara Göre Kalıcı Risk Seviyesi',
    residualByDeptTitle: isEnglish ? 'Residual risk by department:' : 'Departmanlara göre kalıntı risk:',
    residualByDeptDesc: isEnglish ? 'Residual risk is the amount of risk remaining after all risk controls and mitigation efforts have been implemented. In other words, it is the level of exposure that persists even after preventive, detective, and corrective measures are in place. We use residual risk as a KPI for risk awareness, reflecting how well employees and the organization recognize and address potential risks, highlighting a proactive approach to risk management.' : 'Kalıntı risk, tüm risk kontrolleri ve azaltma çabaları uygulandıktan sonra kalan risk miktarıdır. Başka bir deyişle, önleyici, tespit edici ve düzeltici önlemler alındıktan sonra bile devam eden maruziyet seviyesidir. Risk farkındalığının bir KPI\'sı olarak kalıntı riski kullanıyoruz, çalışanların ve kuruluşun potansiyel riskleri nasıl tanıdığını ve ele aldığını, risk yönetimine proaktif bir yaklaşımı vurgulayarak yansıtır.',
    residualByDeptCharts: isEnglish ? 'The charts indicate that marketing and finance departments have higher levels of residual risk, highlighting the need for greater focus and improvement in these departments.' : 'Grafikler, pazarlama ve finans departmanlarının daha yüksek kalıntı risk seviyelerine sahip olduğunu gösteriyor, bu departmanlarda daha fazla odaklanma ve iyileştirme ihtiyacını vurguluyor.',
    riskOverTime: isEnglish ? 'Overall risk level over time:' : 'Zamana göre genel risk seviyesi:',
    riskOverTimeDesc: isEnglish ? 'The above graph shows the company\'s average inherent risks, residual risks, and future risks over 5 quarters. During the reporting period, the company\'s inherent and residual risks have decreased compared to the previous period. Our company has made significant progress in reducing risks.' : 'Yukarıdaki grafik, şirketin 5 çeyrekteki ortalama doğal riskleri, kalıntı riskleri ve gelecek risklerini göstermektedir. Raporlama döneminde, şirketin doğal riskleri ve kalıntı riskleri önceki döneme göre azalmıştır. Şirketimiz riskleri azaltma konusunda ciddi ilerleme kaydetmiştir.',
    riskAppetiteTitle: isEnglish ? 'Risk appetite:' : 'Risk iştahı:',
    riskAppetiteDesc: isEnglish ? 'Our company mostly operates with a minimalist risk appetite, prioritizing only essential and low-impact risks. This lean approach ensures that our risk-taking is deliberate and necessary, aligning with our core objectives and preserving organizational stability.' : 'Şirketimiz çoğunlukla minimalist bir risk iştahıyla çalışır, sadece temel ve düşük etkili risklere öncelik verir. Bu yalın yaklaşım, risk almamızın bilinçli ve gerekli olmasını sağlar, temel hedeflerimizle uyumlu olur ve kurumsal istikrarı korur.',
    
    // Section 7
    itDepartment: isEnglish ? 'IT Department' : 'IT Department',
    actionStatusDesc: isEnglish ? 'In our department, action completion is being monitored on time. Currently, 100.0% of actions are planned. 0% of these actions are overdue by 0-30 days and 0% are overdue by 30-60 days. These figures highlight the importance of operational efficiency and maintaining department objectives.' : 'Departmanımızda, eylemlerin zamanında tamamlanmasını sağlamak için izlenmektedir. Şu anda, 100.0% eylem planlanmıştır. Bu eylemlerin %0\'i 0-30 gün içinde gecikmiştir ve %0\'i 30-60 gün içinde gecikmiştir. Bu rakamlar, gecikmiş eylemleri ele almak için operasyonel verimliliği ve departman hedeflerini korumak için önemini vurgular.',
    riskSummaryDesc: isEnglish ? 'The IT department\'s risk assessment reveals 10 financial risk points, 10 operational risk points, and 13 reputational risk points before controls were implemented, indicating significant risk exposure in these areas. After risk mitigation efforts, the average residual risk scores were successfully reduced to 7, 6, and 6 respectively, reflecting proactive management of existing risks by the department.' : 'IT departmanının risk değerlendirmesi, kontroller uygulanmadan önce bu alanlarda önemli bir risk maruziyetini gösteren 10 finansal risk puanı, 10 operasyonel risk puanı ve 13 itibar riski puanı ortaya koymaktadır. Risk azaltma çalışmalarından sonra, ortalama kalıntı risk puanları sırasıyla 7, 6 ve 6\'e başarıyla düşürülmüş olup, bu durum departmanın mevcut riskleri proaktif yönetimini yansıtmaktadır.',
    topRisksDesc: isEnglish ? 'The following represent the 5 most critical risks our department currently faces: Cyber attacks, Firewall Unauthorized Access Risk, Firewall connection interruption risk, and System Interruption. These risks pose serious threats to our strategic objectives.' : 'Aşağıdakiler, şu anda departmanımızın karşı karşıya olduğu en kritik 5 riski temsil etmektedir: Siber saldırılar, Firewall Yetkisiz Erişim Riski, Firewall erişim kesilme riski ve Sistem Kesintisi. Bu riskler, stratejik hedeflerimiz için ciddi tehditler oluşturmaktadır.',
    financialRiskAssessment: isEnglish ? 'In our financial risk assessment' : 'Finansal risk değerlendirmemizde',
    financialRiskAssessmentDesc: isEnglish ? ', a total of 3 risks were identified. Initially, pre-control risk levels (inherent risk) included 3 high and 0 extreme risks, highlighting significant vulnerabilities. After implementation of control and mitigation measures, residual risk levels were reduced to more acceptable levels: 0 low, 3 moderate, and 0 high risk.' : ', toplam 3 risk tespit edildi. Başlangıçta, kontrol öncesi risk seviyeleri (doğal risk) 3 yüksek ve 0 aşırı risk içeriyordu ve bu önemli kırılganlıkları vurguluyordu. Kontrol ve azaltma önlemlerinin uygulanmasından sonra, kalıntı risk seviyeleri daha kabul edilebilir seviyelere indirildi: 0 düşük, 3 orta ve 0 yüksek risk.',
    operationalRiskAssessment: isEnglish ? 'In our operational risk assessment' : 'Operasyonel risk değerlendirmemizde',
    operationalRiskAssessmentDesc: isEnglish ? ' a total of 3 risks were identified. Initial inherent risk levels included 1 moderate, 1 high, and 1 extreme risk, indicating critical operational vulnerabilities. After implementation of control and mitigation measures, risk levels were reduced to more acceptable levels: 0 low and 2 moderate risk.' : ' toplam 3 risk tespit edildi. Başlangıçtaki doğal risk seviyeleri 1 orta, 1 yüksek ve 1 aşırı risk içeriyordu ve bu kritik operasyonel kırılganlıkları gösteriyordu. Kontrol ve azaltma önlemlerinin uygulanmasından sonra, risk seviyeleri daha kabul edilebilir seviyelere indirildi: 0 düşük ve 2 orta risk.',
    reputationalRiskAssessment: isEnglish ? 'In our reputational risk assessment' : 'İtibar riski değerlendirmemizde',
    reputationalRiskAssessmentDesc: isEnglish ? ' a total of 3 risks were identified. Initially, inherent risk levels included 2 high and 1 extreme risk, reflecting significant threats to our reputation. After implementation of control and mitigation measures, risks were reduced to more acceptable levels: 0 low, 3 moderate, and 0 high risk.' : ' toplam 3 risk tespit edildi. Başlangıçta, doğal risk seviyeleri 2 yüksek ve 1 aşırı riski içeriyordu ve bu itibarımıza yönelik önemli tehditleri yansıtıyordu. Kontrol ve azaltma önlemlerinin uygulanmasından sonra, riskler daha kabul edilebilir seviyelere indirildi: 0 düşük, 3 orta ve 0 yüksek risk.',
    redBanner: isEnglish ? 'Protecting Against Major Threats, Securing Today, Safeguarding Tomorrow' : 'En Büyük Tehditleri Koruyarak Bugünü Koruyarak, Yarını Koruyarak',
    
    // Conclusion
    conclusionIntro: isEnglish ? 'As part of our ongoing risk management efforts, the following provides a summary of the key findings, actions taken, and future steps for addressing financial, operational, and reputational risks.' : 'Süregelen risk yönetimi çabalarımızın bir parçası olarak, aşağıda finansal, operasyonel ve itibar risklerini ele almak için temel bulgular, alınan eylemler ve gelecek adımların bir özeti sunulmaktadır.',
    conclusion1: isEnglish ? '1. A comprehensive assessment of the organization\'s financial, operational, and reputational risks has been completed, providing a clear view of the current risk landscape.' : '1. Kuruluşun finansal, operasyonel ve itibar risklerinin kapsamlı bir değerlendirmesi tamamlanmış, mevcut risk ortamının net bir görünümü sağlanmıştır.',
    conclusion2: isEnglish ? '2. The effectiveness of implemented controls has been evaluated, with significant reductions in risk levels observed. Many risks have been successfully reduced from high to moderate or low levels, indicating that control measures have been largely effective.' : '2. Uygulanan kontrollerin etkinliği değerlendirilmiş, risk seviyelerinde önemli azalmalar görülmüştür. Birçok risk başarıyla yüksek seviyeden orta veya düşük seviyelere indirilmiş, bu da kontrol önlemlerinin büyük ölçüde etkili olduğunu göstermektedir.',
    conclusion3: isEnglish ? '3. Despite the progress made, certain risks, particularly in operational and reputational areas, still require ongoing attention. These areas require continuous monitoring and improvement of controls to align with the company\'s risk tolerance and ensure long-term stability.' : '3. Kaydedilen ilerlemeye rağmen, özellikle operasyonel ve itibar alanlarındaki bazı riskler sürekli dikkat gerektirmektedir. Bu alanlar, şirketin risk toleransı ile uyumlu olmak ve uzun vadeli istikrarı sağlamak için kontrollerin sürekli izlenmesini ve iyileştirilmesini gerektirmektedir.',
    conclusion4: isEnglish ? '4. Specific recommendations have been provided to address remaining high-risk areas. These recommendations focus on improving existing controls and implementing new measures when necessary to further reduce the likelihood and impact of these risks.' : '4. Kalan yüksek riskli alanları ele almak için özel öneriler sunulmuştur. Bu öneriler, bu risklerin olasılığını ve etkisini daha da azaltmak için mevcut kontrolleri geliştirmeye ve gerektiğinde yeni önlemler uygulamaya odaklanmaktadır.',
    conclusion5: isEnglish ? '5. An action plan has been established with clearly defined actions and timelines for each risk area. These actions aim to further reduce risks and ensure they remain at acceptable levels.' : '5. Her risk alanı için açıkça tanımlanmış eylemler ve zaman çizelgeleri içeren bir eylem planı oluşturulmuştur. Bu eylemler, riskleri daha da azaltmayı ve kabul edilebilir seviyelerde kalmalarını sağlamayı amaçlamaktadır.',
    conclusion6: isEnglish ? '6. Looking forward, the organization will continue to prioritize risk management, focusing on keeping risks at acceptable levels. Any changes in the risk landscape will be promptly addressed to ensure the company\'s risk exposure remains within acceptable limits.' : '6. İleriye dönük olarak, kuruluş risk yönetimine öncelik vermeye, riskleri kabul edilebilir seviyelerde tutmaya odaklanmaya devam edecektir. Risk ortamındaki herhangi bir değişiklik, şirketin risk maruziyetinin kabul edilebilir sınırlar içinde kalmasını sağlamak için hızla ele alınacaktır.',
    regards: isEnglish ? 'Best regards' : 'Saygılarımızla',
    
    // Outro Hero
    outroTitle: 'FIN AND FIND RISK TEAM',
    outroSubtitle: isEnglish ? 'Empowering Risk Management for Decision-Making – Together Towards a Confident Future' : 'Karar Vermeye İzin Verici Risk Yönetimi – Birlikte Geleceğe Güven',
  };

  return [
    {
      type: CUSTOM_ELEMENT_TYPES.HERO,
      id: `hero-${ts}`,
      title: t.title,
      subtitle: t.subtitle,
      backgroundImage: 'mountain',
      overlayColor: '#bb133e',
      overlayOpacity: 0.75,
      children: [{ text: '' }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-1-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    {
      type: 'h2',
      children: [{ text: t.page1title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page1subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page1subtitle2, bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page1subtitle3 }],
    },
    {
      type: 'column_group',
      id: `columns-exec-${ts}`,
      preset: '2-equal',
      gap: 16,
      children: [
        {
          type: 'column',
          id: `col-exec-1-${ts}`,
          width: '50%',
          children: [
            {
              type: 'p',
              children: [{ text: t.page1subtitle4, color: '#bb133e', bold: true }],
            },
            {
              type: 'p',
              children: [{ text: t.page1subtitle5 }],
            },
          ],
        },
        {
          type: 'column',
          id: `col-exec-2-${ts}`,
          width: '50%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
              id: `img-page1chart-${ts}`,
              src: 'page1chart',
              alt: 'Executive Summary Word Cloud',
              children: [{ text: '' }],
            },
          ],
        },
      ],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-2-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    {
      type: 'h2',
      children: [{ text: t.page2title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page2subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page2subtitle2 }],
    },
    // Section 2 - 3-column layout with world map
    {
      type: 'column_group',
      id: `columns-page2-${ts}`,
      preset: '3-equal',
      gap: 12,
      children: [
        {
          type: 'column',
          id: `col-page2-1-${ts}`,
          width: '33.33%',
          children: [
            {
              type: 'p',
              children: [{ text: t.page2subtitle3, bold: true }],
            },
            {
              type: 'p',
              children: [{ text: t.page2subtitle4 }],
            },
          ],
        },
        {
          type: 'column',
          id: `col-page2-2-${ts}`,
          width: '33.33%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
              id: `img-page2chart-${ts}`,
              src: 'page2chart',
              alt: 'World Risk Map',
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: t.page2subtitle7, bold: true }],
            },
            {
              type: 'p',
              children: [{ text: t.page2subtitle8 }],
            },
          ],
        },
        {
          type: 'column',
          id: `col-page2-3-${ts}`,
          width: '33.34%',
          children: [
            {
              type: 'p',
              children: [{ text: t.page2subtitle5, bold: true }],
            },
            {
              type: 'p',
              children: [{ text: t.page2subtitle6 }],
            },
          ],
        },
      ],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-page3-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 3 - Strategy and Objective
    {
      type: 'h2',
      children: [{ text: t.page3title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page3subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page3subtitle2, bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page3subtitle3 }],
    },
    // Risk Appetite - 2 column layout with gauge chart and colored bullets
    {
      type: 'column_group',
      id: `columns-page3-${ts}`,
      preset: '2-equal',
      gap: 24,
      children: [
        {
          type: 'column',
          id: `col-page3-1-${ts}`,
          width: '40%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
              id: `img-page3chart-${ts}`,
              src: 'page3chart',
              alt: 'Risk Appetite Gauge',
              children: [{ text: '' }],
            },
          ],
        },
        {
          type: 'column',
          id: `col-page3-2-${ts}`,
          width: '60%',
          children: [
            {
              type: 'ul',
              children: [
                { type: 'li', circleType: 'averse', children: [{ text: txt.averse, bold: true }, { text: txt.averseDesc }] },
                { type: 'li', circleType: 'minimalist', children: [{ text: txt.minimalist, bold: true }, { text: txt.minimalistDesc }] },
                { type: 'li', circleType: 'cautious', children: [{ text: txt.cautious, bold: true }, { text: txt.cautiousDesc }] },
                { type: 'li', circleType: 'open', children: [{ text: txt.open, bold: true }, { text: txt.openDesc }] },
                { type: 'li', circleType: 'hungry', children: [{ text: txt.hungry, bold: true }, { text: txt.hungryDesc }] },
              ],
            },
          ],
        },
      ],
    },
    // Risk Category Definition
    {
      type: 'p',
      children: [{ text: t.page3subtitle5, bold: true }],
    },
    {
      type: 'ul',
      children: [
        { type: 'li', children: [{ text: txt.operationalRisks, bold: true }, { text: txt.operationalRisksDesc }] },
        { type: 'li', children: [{ text: txt.financialRisks, bold: true }, { text: txt.financialRisksDesc }] },
        { type: 'li', children: [{ text: txt.strategicRisks, bold: true }, { text: txt.strategicRisksDesc }] },
        { type: 'li', children: [{ text: txt.complianceRisks, bold: true }, { text: txt.complianceRisksDesc }] },
        { type: 'li', children: [{ text: txt.reputationalRisks, bold: true }, { text: txt.reputationalRisksDesc }] },
      ],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-page4-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 4 - Performance
    {
      type: 'h2',
      children: [{ text: t.page4title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page4subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'column_group',
      id: `columns-page4-${ts}`,
      preset: '2-equal',
      gap: 24,
      children: [
        {
          type: 'column',
          id: `col-page4-1-${ts}`,
          width: '50%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
              id: `img-page4chart-${ts}`,
              src: 'page4chart',
              alt: 'Risk Method Diagram',
              children: [{ text: '' }],
            },
          ],
        },
        {
          type: 'column',
          id: `col-page4-2-${ts}`,
          width: '50%',
          children: [
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.inherentRisk, bold: true }, { text: txt.inherentRiskDesc }],
            },
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.residualRisk, bold: true }, { text: txt.residualRiskDesc }],
            },
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.futureRisk, bold: true }, { text: txt.futureRiskDesc }],
            },
            {
              type: 'p',
              children: [{ text: txt.keyDistinction, bold: true }],
            },
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.inherentRisk, bold: true }, { text: txt.inherentShort }],
            },
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.residualRisk, bold: true }, { text: txt.residualShort }],
            },
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.futureRisk, bold: true }, { text: txt.futureShort }],
            },
          ],
        },
      ],
    },
    {
      type: 'p',
      children: [{ text: txt.likelihood, bold: true }, { text: txt.likelihoodDesc }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-likelihood-${ts}`,
      mode: 'likelihood',
      title: 'Likelihood Scale',
      children: [{ text: '' }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-page5-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 5 - Key Risk Categories
    {
      type: 'h2',
      children: [{ text: t.page5title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page5subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page5subtitle2, bold: true }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-severity-${ts}`,
      mode: 'severity',
      title: 'Severity Scale',
      children: [{ text: '' }],
    },
    {
      type: 'p',
      children: [{ text: t.page5subtitle4 }],
    },
    // Risk Matrix - 2 column layout
    {
      type: 'column_group',
      id: `columns-page5-${ts}`,
      preset: '2-equal',
      gap: 24,
      children: [
        {
          type: 'column',
          id: `col-page5-1-${ts}`,
          width: '50%',
          children: [
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.purpose, bold: true }, { text: txt.purposeDesc }],
            },
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.matrixDesign, bold: true }, { text: txt.matrixDesignDesc }],
            },
            {
              type: 'p',
              children: [{ text: '• ', bold: true }, { text: txt.prioritization, bold: true }, { text: txt.prioritizationDesc }],
            },
          ],
        },
        {
          type: 'column',
          id: `col-page5-2-${ts}`,
          width: '50%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
              id: `img-page5chart-${ts}`,
              src: 'page5chart',
              alt: 'Risk Matrix',
              children: [{ text: '' }],
            },
          ],
        },
      ],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-charts-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 6 - Company Risk Level
    {
      type: 'h2',
      children: [{ text: t.page6title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page6subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page6subtitle2 }],
    },
    // Two-column layout for Section 6
    {
      type: 'column_group',
      id: `columns-page6-${ts}`,
      preset: '2-equal',
      gap: 24,
      children: [
        // LEFT COLUMN - Risk distribution and residual risk
        {
          type: 'column',
          id: `col-page6-1-${ts}`,
          width: '50%',
          children: [
            {
              type: 'p',
              children: [{ text: txt.riskDistribution, bold: true }],
            },
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-risk-by-dept-${ts}`,
              chartType: 'RiskByDepartment',
              height: 220,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.riskDistributionDesc }],
            },
            {
              type: 'p',
              children: [{ text: txt.residualByDept, bold: true }],
            },
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-residual-by-dept-${ts}`,
              chartType: 'RiskLevelByDepartment',
              height: 200,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.residualByDeptTitle, bold: true }],
            },
            {
              type: 'p',
              children: [{ text: txt.residualByDeptDesc }],
            },
            {
              type: 'p',
              children: [{ text: txt.residualByDeptCharts }],
            },
          ],
        },
        // RIGHT COLUMN - Risk over time and appetite
        {
          type: 'column',
          id: `col-page6-2-${ts}`,
          width: '50%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-risk-line-${ts}`,
              chartType: 'RiskLineChart',
              height: 250,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.riskOverTime, bold: true }],
            },
            {
              type: 'p',
              children: [{ text: txt.riskOverTimeDesc }],
            },
            {
              type: 'p',
              children: [{ text: txt.riskAppetiteTitle, bold: true }],
            },
            {
              type: 'p',
              children: [{ text: txt.riskAppetiteDesc }],
            },
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-risk-appetite-page6-${ts}`,
              chartType: 'RiskAppetite',
              height: 250,
              children: [{ text: '' }],
            },
          ],
        },
      ],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-page7-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 7 - Department Detail (First part - Overview)
    {
      type: 'h2',
      children: [{ text: t.page7title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: txt.itDepartment, bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page7subtitle1 }],
    },
    // 3-column layout for department overview
    {
      type: 'column_group',
      id: `columns-page7-overview-${ts}`,
      preset: '3-equal',
      gap: 16,
      children: [
        // LEFT: Action Status
        {
          type: 'column',
          id: `col-page7-1-${ts}`,
          width: '33.33%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-action-status-${ts}`,
              chartType: 'ActionStatus',
              height: 180,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.actionStatusDesc }],
            },
          ],
        },
        // CENTER: Risk Summary Report
        {
          type: 'column',
          id: `col-page7-2-${ts}`,
          width: '33.33%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-risk-summary-dept-${ts}`,
              chartType: 'RiskSummaryReport',
              reportType: 'financial',
              height: 180,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.riskSummaryDesc }],
            },
          ],
        },
        // RIGHT: Top Risks
        {
          type: 'column',
          id: `col-page7-3-${ts}`,
          width: '33.34%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-top-risks-${ts}`,
              chartType: 'TopRisks',
              height: 180,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.topRisksDesc }],
            },
          ],
        },
      ],
    },
    // Red Banner
    {
      type: 'p',
      children: [{ text: txt.redBanner, color: '#ffffff', bold: true }],
      backgroundColor: '#bb133e',
      textAlign: 'center',
      padding: '12px',
    },
    // Section 7 - Department Detail (Second part - Risk Breakdown)
    {
      type: 'h2',
      children: [{ text: t.page7title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: txt.itDepartment, bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page7subtitle3 }],
    },
    // 3-column layout for risk breakdown charts
    {
      type: 'column_group',
      id: `columns-page7-breakdown-${ts}`,
      preset: '3-equal',
      gap: 16,
      children: [
        // Financial Risk
        {
          type: 'column',
          id: `col-page7-fin-${ts}`,
          width: '33.33%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-risk-fin-${ts}`,
              chartType: 'RiskSummary',
              height: 200,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.financialRiskAssessment, bold: true }, { text: txt.financialRiskAssessmentDesc }],
            },
          ],
        },
        // Operational Risk
        {
          type: 'column',
          id: `col-page7-op-${ts}`,
          width: '33.33%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-risk-op-${ts}`,
              chartType: 'RiskSummary',
              height: 200,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.operationalRiskAssessment, bold: true }, { text: txt.operationalRiskAssessmentDesc }],
            },
          ],
        },
        // Reputational Risk
        {
          type: 'column',
          id: `col-page7-rep-${ts}`,
          width: '33.34%',
          children: [
            {
              type: CUSTOM_ELEMENT_TYPES.CHART,
              id: `chart-risk-rep-${ts}`,
              chartType: 'RiskSummary',
              height: 200,
              children: [{ text: '' }],
            },
            {
              type: 'p',
              children: [{ text: txt.reputationalRiskAssessment, bold: true }, { text: txt.reputationalRiskAssessmentDesc }],
            },
          ],
        },
      ],
    },
    // Red Banner
    {
      type: 'p',
      children: [{ text: txt.redBanner, color: '#ffffff', bold: true }],
      backgroundColor: '#bb133e',
      textAlign: 'center',
      padding: '12px',
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-tables-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 8 - Low Risk
    {
      type: 'h2',
      children: [{ text: t.page8title, color: '#bb133e', bold: true }],
    },
    // 8.1 Risk Assessment and Analysis
    {
      type: 'p',
      children: [{ text: t.page8subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page8subtitle2 }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-low-risk-${ts}`,
      mode: 'low_risks',
      title: 'Low Risk Assessment',
      children: [{ text: '' }],
    },
    {
      type: 'p',
      children: [{ text: t.page8subtitle3 }],
    },
    // 8.2 Controls and Mitigations
    {
      type: 'h2',
      children: [{ text: t.page8title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page8subtitle4, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page8subtitle5 }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-low-risk-controls-${ts}`,
      mode: 'low_risk_controls',
      title: 'Low Risk Controls',
      children: [{ text: '' }],
    },
    // 8.3 Action Plan and Timeline
    {
      type: 'h2',
      children: [{ text: t.page8title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page8subtitle6, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page8subtitle7 }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-low-risk-actions-${ts}`,
      mode: 'low_risk_actions',
      title: 'Low Risk Actions',
      children: [{ text: '' }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-page9-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 9 - High Risk
    {
      type: 'h2',
      children: [{ text: t.page9title, color: '#bb133e', bold: true }],
    },
    // 9.1 Risk Assessment and Analysis
    {
      type: 'p',
      children: [{ text: t.page9subtitle1, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page9subtitle2 }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-high-risk-${ts}`,
      mode: 'high_risks',
      title: 'High Risk Assessment',
      children: [{ text: '' }],
    },
    {
      type: 'p',
      children: [{ text: t.page9subtitle3 }],
    },
    // 9.2 Controls and Mitigations
    {
      type: 'h2',
      children: [{ text: t.page9title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page9subtitle4, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page9subtitle5 }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-high-risk-controls-${ts}`,
      mode: 'high_risk_controls',
      title: 'High Risk Controls',
      children: [{ text: '' }],
    },
    // 9.3 Action Plan and Timeline
    {
      type: 'h2',
      children: [{ text: t.page9title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page9subtitle6, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page9subtitle7 }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-high-risk-actions-${ts}`,
      mode: 'high_risk_actions',
      title: 'High Risk Actions',
      children: [{ text: '' }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-page10-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Section 10 - Incidents
    {
      type: 'h2',
      children: [{ text: t.page10title, color: '#bb133e', bold: true }],
    },
    {
      type: 'p',
      children: [{ text: t.page10subtitle1 }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      id: `table-incidents-${ts}`,
      mode: 'incidents',
      title: 'Incidents',
      children: [{ text: '' }],
    },
    {
      type: CUSTOM_ELEMENT_TYPES.DIVIDER,
      id: `divider-conclusion-${ts}`,
      style: 'solid',
      thickness: 1,
      color: '#e5e7eb',
      spacing: 24,
      children: [{ text: '' }],
    },
    // Conclusion Section
    {
      type: 'h2',
      children: [{ text: t.page11title, color: '#bb133e', bold: true }],
    },
    // Conclusion box with border
    {
      type: 'p',
      children: [{ text: txt.conclusionIntro }],
      border: '1px solid #bb133e',
      padding: '16px',
      marginBottom: '0',
    },
    {
      type: 'p',
      children: [{ text: txt.conclusion1 }],
      border: '1px solid #bb133e',
      borderTop: 'none',
      padding: '16px',
      paddingTop: '8px',
      marginTop: '0',
      marginBottom: '0',
    },
    {
      type: 'p',
      children: [{ text: txt.conclusion2 }],
      border: '1px solid #bb133e',
      borderTop: 'none',
      padding: '16px',
      paddingTop: '8px',
      marginTop: '0',
      marginBottom: '0',
    },
    {
      type: 'p',
      children: [{ text: txt.conclusion3 }],
      border: '1px solid #bb133e',
      borderTop: 'none',
      padding: '16px',
      paddingTop: '8px',
      marginTop: '0',
      marginBottom: '0',
    },
    {
      type: 'p',
      children: [{ text: txt.conclusion4 }],
      border: '1px solid #bb133e',
      borderTop: 'none',
      padding: '16px',
      paddingTop: '8px',
      marginTop: '0',
      marginBottom: '0',
    },
    {
      type: 'p',
      children: [{ text: txt.conclusion5 }],
      border: '1px solid #bb133e',
      borderTop: 'none',
      padding: '16px',
      paddingTop: '8px',
      marginTop: '0',
      marginBottom: '0',
    },
    {
      type: 'p',
      children: [{ text: txt.conclusion6 }],
      border: '1px solid #bb133e',
      borderTop: 'none',
      padding: '16px',
      paddingTop: '8px',
      marginTop: '0',
    },
    // Signature
    {
      type: 'p',
      children: [{ text: txt.regards }],
    },
    {
      type: 'p',
      children: [{ text: new Date().toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR') }],
    },
    // Outro Hero
    {
      type: CUSTOM_ELEMENT_TYPES.HERO,
      id: `hero-outro-${ts}`,
      title: txt.outroTitle,
      subtitle: txt.outroSubtitle,
      backgroundType: 'color',
      backgroundColor: '#a11043',
      overlayOpacity: 0.3,
      titleStyle: {
        color: '#bb133e',
        size: '3xl',
        bold: true,
      },
      subtitleStyle: {
        color: '#ffffff',
        size: 'lg',
        bold: false,
      },
      children: [{ text: '' }],
    },
  ];
}

// ============================================
// CUSTOM EDITOR SETUP
// ============================================

const withCustomVoids = (editor) => {
  const { isVoid } = editor;

  editor.isVoid = (element) => {
    const voidTypes = [
      CUSTOM_ELEMENT_TYPES.CHART,
      CUSTOM_ELEMENT_TYPES.DATA_TABLE,
      CUSTOM_ELEMENT_TYPES.HERO,
      CUSTOM_ELEMENT_TYPES.DIVIDER,
      CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
    ];
    return voidTypes.includes(element.type) ? true : isVoid(element);
  };

  return editor;
};

// ============================================
// MAIN EDITOR COMPONENT
// ============================================

function PlateReportEditor({
  initialBlocks = [],
  initialLanguage = 'tr',
  chartData = {},
  reportData = {},
  activeDepartment = '',
  onSave,
  onClose,
  isNew = false,
}) {
  // Create initial value
  const initialValue = useMemo(() => {
    if (initialBlocks && initialBlocks.length > 0) {
      return convertBlocksToPlateValue(initialBlocks);
    }
    const t = translations[initialLanguage];
    return createDefaultTemplate(t, initialLanguage);
  }, [initialBlocks, initialLanguage]);

  // State
  const [language, setLanguage] = useState(initialLanguage);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [, forceUpdate] = useState(0);

  // Create editor
  const editor = useMemo(() => {
    return withCustomVoids(withHistory(withReact(createEditor())));
  }, []);

  // Force re-render after initial mount to fix Slate layout issues
  useEffect(() => {
    const timer = setTimeout(() => {
      // Force a re-render by updating state
      forceUpdate(n => n + 1);
      // Also normalize the editor to ensure proper rendering
      Editor.normalize(editor, { force: true });
    }, 50);
    return () => clearTimeout(timer);
  }, [editor]);

  // Handle change - just track dirty state
  const handleChange = useCallback(() => {
    setIsDirty(true);
  }, []);

  // Column presets for updating column layouts
  const COLUMN_PRESETS = {
    '2-equal': { widths: ['50%', '50%'] },
    '2-left': { widths: ['66%', '34%'] },
    '2-right': { widths: ['34%', '66%'] },
    '3-equal': { widths: ['33.33%', '33.33%', '33.34%'] },
    '4-equal': { widths: ['25%', '25%', '25%', '25%'] },
  };

  // Update element - find by ID and update using Slate Transforms
  const handleUpdateElement = useCallback((elementId, updates) => {
    // Find the path of the element with the given ID
    const findNodePath = (nodes, path = []) => {
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const currentPath = [...path, i];
        
        if (node.id === elementId) {
          return currentPath;
        }
        
        if (node.children && Array.isArray(node.children)) {
          const foundPath = findNodePath(node.children, currentPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };

    const nodePath = findNodePath(editor.children);
    if (nodePath) {
      // Check if this is a preset change for a column_group
      const [node] = Editor.node(editor, nodePath);
      
      if (node.type === 'column_group' && updates.preset && COLUMN_PRESETS[updates.preset]) {
        const newPreset = COLUMN_PRESETS[updates.preset];
        const newWidths = newPreset.widths;
        const currentColumns = node.children.filter(c => c.type === 'column');
        const currentCount = currentColumns.length;
        const newCount = newWidths.length;
        
        // Update the preset property
        Transforms.setNodes(editor, { preset: updates.preset }, { at: nodePath });
        
        // Update existing column widths
        for (let i = 0; i < Math.min(currentCount, newCount); i++) {
          const columnPath = [...nodePath, i];
          Transforms.setNodes(editor, { width: newWidths[i] }, { at: columnPath });
        }
        
        // Add new columns if needed
        if (newCount > currentCount) {
          for (let i = currentCount; i < newCount; i++) {
            const ts = Date.now();
            const newColumn = {
              type: 'column',
              id: `col-${i + 1}-${ts}`,
              width: newWidths[i],
              children: [{ type: 'p', children: [{ text: '' }] }],
            };
            Transforms.insertNodes(editor, newColumn, { at: [...nodePath, i] });
          }
        }
        
        // Remove extra columns if needed
        if (newCount < currentCount) {
          // Remove from the end first
          for (let i = currentCount - 1; i >= newCount; i--) {
            Transforms.removeNodes(editor, { at: [...nodePath, i] });
          }
        }
      } else {
        // Normal update
        Transforms.setNodes(editor, updates, { at: nodePath });
      }
    }
    setIsDirty(true);
  }, [editor]);

  // Delete element - find by ID and remove using Slate Transforms
  const handleDeleteElement = useCallback((elementId) => {
    // Find the path of the element with the given ID
    const findNodePath = (nodes, path = []) => {
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const currentPath = [...path, i];
        
        if (node.id === elementId) {
          return currentPath;
        }
        
        if (node.children && Array.isArray(node.children)) {
          const foundPath = findNodePath(node.children, currentPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };

    const nodePath = findNodePath(editor.children);
    if (nodePath) {
      Transforms.removeNodes(editor, { at: nodePath });
    }
    setIsDirty(true);
  }, [editor]);

  // Add block - using Slate Transforms to properly insert nodes
  const handleAddBlock = useCallback((type) => {
    const ts = Date.now();
    let newNode;

    switch (type) {
      case 'text':
        newNode = { type: 'p', children: [{ text: 'Start typing here...' }] };
        break;
      case 'chart':
        newNode = {
          type: CUSTOM_ELEMENT_TYPES.CHART,
          id: `chart-${ts}`,
          chartType: null,
          height: 300,
          children: [{ text: '' }],
        };
        break;
      case 'table':
        newNode = {
          type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
          id: `table-${ts}`,
          mode: 'custom',
          tableData: {
            headers: ['Column 1', 'Column 2', 'Column 3'],
            rows: [['', '', '']],
          },
          children: [{ text: '' }],
        };
        break;
      case 'hero':
        newNode = {
          type: CUSTOM_ELEMENT_TYPES.HERO,
          id: `hero-${ts}`,
          title: 'Enter Title',
          subtitle: 'Enter subtitle',
          backgroundImage: 'mountain',
          overlayColor: '#bb133e',
          overlayOpacity: 0.75,
          children: [{ text: '' }],
        };
        break;
      case 'divider':
        newNode = {
          type: CUSTOM_ELEMENT_TYPES.DIVIDER,
          id: `divider-${ts}`,
          style: 'solid',
          thickness: 1,
          color: '#e5e7eb',
          spacing: 16,
          children: [{ text: '' }],
        };
        break;
      case 'image':
        newNode = {
          type: CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
          id: `image-${ts}`,
          src: null,
          alt: '',
          children: [{ text: '' }],
        };
        break;
      case 'columns':
        newNode = {
          type: 'column_group',
          id: `columns-${ts}`,
          preset: '2-equal',
          gap: 16,
          children: [
            {
              type: 'column',
              id: `col-1-${ts}`,
              width: '50%',
              children: [{ type: 'p', children: [{ text: '' }] }],
            },
            {
              type: 'column',
              id: `col-2-${ts}`,
              width: '50%',
              children: [{ type: 'p', children: [{ text: '' }] }],
            },
          ],
        };
        break;
      default:
        newNode = { type: 'p', children: [{ text: '' }] };
    }

    // Get current selection to insert at cursor position
    const { selection } = editor;
    
    if (selection) {
      const path = selection.anchor.path;
      
      // Check if we're inside a column (path would be like [x, y, z] where y is column index)
      // Path structure: [groupIndex, columnIndex, paragraphIndex, ...]
      const isInsideColumn = path.length >= 2 && (() => {
        try {
          const parentPath = path.slice(0, 2);
          const [parentNode] = Editor.node(editor, parentPath.slice(0, 1));
          return parentNode?.type === 'column_group';
        } catch {
          return false;
        }
      })();
      
      // For columns block type, always insert at top level
      // For other blocks, insert inside column if cursor is there
      if (type === 'columns' || !isInsideColumn) {
        // Insert at top level after current block
        const topLevelPath = [path[0]];
        const insertPath = [topLevelPath[0] + 1];
        Transforms.insertNodes(editor, newNode, { at: insertPath });
        Transforms.select(editor, Editor.start(editor, insertPath));
      } else {
        // Insert inside the column at the current paragraph's position
        // path = [groupIndex, columnIndex, paragraphIndex, ...]
        const insertPath = [...path.slice(0, 3)];
        insertPath[2] = insertPath[2] + 1; // Insert after current paragraph in column
        Transforms.insertNodes(editor, newNode, { at: insertPath });
        Transforms.select(editor, Editor.start(editor, insertPath));
      }
    } else {
      // No selection - insert at end
      Transforms.insertNodes(editor, newNode, { at: [editor.children.length] });
      Transforms.select(editor, Editor.end(editor, []));
    }
    
    setIsDirty(true);
    setShowBlockMenu(false);
  }, [editor]);

  // Save - use editor.children as source of truth
  const handleSave = useCallback(async () => {
    if (!onSave) return;

    setIsLoading(true);
    setSaveStatus('saving');

    try {
      // Get current content from the editor (source of truth)
      const blocks = convertPlateValueToBlocks(editor.children);
      await onSave(blocks, language);
      setIsDirty(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Error saving report:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsLoading(false);
    }
  }, [editor, language, onSave]);

  // Close
  const handleClose = useCallback(() => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Close without saving?')) {
        onClose && onClose(null);
      }
    } else {
      onClose && onClose(null);
    }
  }, [isDirty, onClose]);

  // Save and close - save first, then just close without passing data
  // (to avoid double-save in parent component)
  const handleSaveAndClose = useCallback(async () => {
    if (isDirty) {
      await handleSave();
    }
    // Just close without passing data - the save was already done above
    onClose && onClose(null);
  }, [isDirty, handleSave, onClose]);

  // Language change - properly reset editor content using Slate Transforms
  const handleLanguageChange = useCallback((newLanguage) => {
    if (isDirty) {
      const confirmed = window.confirm(
        'Switching language will regenerate the report content. Your current changes will be replaced. Continue?'
      );
      if (!confirmed) return;
    }

    // Get new template
    const t = translations[newLanguage];
    const newContent = createDefaultTemplate(t, newLanguage);

    // Clear all existing content using Slate Transforms
    // First, select all
    Transforms.select(editor, {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    });
    
    // Delete all nodes except one (Slate requires at least one node)
    const totalNodes = editor.children.length;
    for (let i = totalNodes - 1; i >= 0; i--) {
      Transforms.removeNodes(editor, { at: [i] });
    }
    
    // Insert new content
    Transforms.insertNodes(editor, newContent, { at: [0] });
    
    // Move cursor to start
    Transforms.select(editor, Editor.start(editor, []));

    setLanguage(newLanguage);
    setIsDirty(true);
  }, [isDirty, editor]);

  // Render element
  const renderElement = useMemo(() => 
    createRenderElement({
      chartData,
      reportData,
      onUpdateElement: handleUpdateElement,
      onDeleteElement: handleDeleteElement,
      language,
    }), [chartData, reportData, handleUpdateElement, handleDeleteElement, language]
  );

  // Render leaf
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  // Block types
  const blockTypes = [
    { type: 'text', icon: FileText, label: 'Text Block' },
    { type: 'image', icon: Image, label: 'Image' },
    { type: 'chart', icon: BarChart3, label: 'Chart' },
    { type: 'table', icon: Table, label: 'Table' },
    { type: 'columns', icon: Columns, label: 'Columns' },
    { type: 'hero', icon: Layout, label: 'Hero Section' },
    { type: 'divider', icon: Minus, label: 'Divider' },
  ];

  // Keyboard shortcuts
  const handleKeyDown = useCallback((event) => {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'b':
          event.preventDefault();
          toggleMark(editor, 'bold');
          break;
        case 'i':
          event.preventDefault();
          toggleMark(editor, 'italic');
          break;
        case 'u':
          event.preventDefault();
          toggleMark(editor, 'underline');
          break;
        case 's':
          event.preventDefault();
          handleSave();
          break;
        case 'z':
          event.preventDefault();
          if (event.shiftKey) {
            editor.redo();
          } else {
            editor.undo();
          }
          break;
        default:
          break;
      }
    }
  }, [editor, handleSave]);

  return (
    <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
      <div className="plate-report-editor flex flex-col h-screen bg-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b shadow-sm flex-shrink-0">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-gray-800">Report Editor</h1>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">{activeDepartment}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Status */}
            <div className="flex items-center gap-2 mr-4">
              {isDirty && !saveStatus && (
                <span className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle size={16} />
                  Unsaved
                </span>
              )}
              {saveStatus === 'saving' && (
                <span className="text-sm text-blue-600 flex items-center gap-1">
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </span>
              )}
              {saveStatus === 'saved' && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle size={16} />
                  Saved!
                </span>
              )}
            </div>
            <button 
              onClick={handleSaveAndClose}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              Save & Close
            </button>
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white border-b shadow-sm flex-shrink-0 sticky top-0 z-40">
          {/* Top toolbar row */}
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <div className="flex items-center gap-2">
              {/* Add Block */}
              <div className="relative">
                <button
                  onClick={() => setShowBlockMenu(!showBlockMenu)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  <Plus size={18} />
                  Add Block
                </button>
                {showBlockMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowBlockMenu(false)} />
                    <div className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-2xl border z-20 min-w-[200px] py-2">
                      {blockTypes.map(({ type, icon: Icon, label }) => (
                        <button
                          key={type}
                          onClick={() => handleAddBlock(type)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                        >
                          <Icon size={18} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">{label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <ToolbarDivider />

              {/* Preview */}
              <button
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors font-medium text-sm ${
                  isPreviewMode 
                    ? 'bg-green-100 text-green-700' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                {isPreviewMode ? <Edit3 size={18} /> : <Eye size={18} />}
                {isPreviewMode ? 'Edit' : 'Preview'}
              </button>
            </div>

            <div className="flex items-center gap-2">
              {/* Language */}
              <div className="flex items-center gap-2">
                <Languages size={18} className="text-gray-500" />
                <select
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en">English</option>
                  <option value="tr">Türkçe</option>
                </select>
              </div>

              <ToolbarDivider />

              {/* Save */}
              <button
                onClick={handleSave}
                disabled={isLoading || !isDirty}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm ${
                  isLoading || !isDirty
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                Save
              </button>
            </div>
          </div>

          {/* Formatting toolbar row */}
          {!isPreviewMode && (
            <Toolbar>
              {/* Undo/Redo */}
              <Button
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.undo();
                }}
                title="Undo (Ctrl+Z)"
              >
                <Undo size={18} />
              </Button>
              <Button
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.redo();
                }}
                title="Redo (Ctrl+Shift+Z)"
              >
                <Redo size={18} />
              </Button>

              <ToolbarDivider />

              {/* Headings */}
              <HeadingSelector />

              <ToolbarDivider />

              {/* Font Size */}
              <FontSizeSelector />

              <ToolbarDivider />

              {/* Text Formatting */}
              <MarkButton format="bold" icon={Bold} />
              <MarkButton format="italic" icon={Italic} />
              <MarkButton format="underline" icon={Underline} />
              <MarkButton format="strikethrough" icon={Strikethrough} />

              <ToolbarDivider />

              {/* Colors */}
              <ColorButton format="color" icon={Type} title="Text Color" defaultColor="#000000" />
              <ColorButton format="backgroundColor" icon={Palette} title="Highlight" defaultColor="#FFFF00" />

              <ToolbarDivider />

              {/* Alignment */}
              <BlockButton format="left" icon={AlignLeft} />
              <BlockButton format="center" icon={AlignCenter} />
              <BlockButton format="right" icon={AlignRight} />

              <ToolbarDivider />

              {/* Lists */}
              <BlockButton format="ul" icon={List} />
              <BlockButton format="ol" icon={ListOrdered} />

              <ToolbarDivider />

              {/* Block Types */}
              <BlockButton format="blockquote" icon={Quote} />
              <BlockButton format="code" icon={Code} />
            </Toolbar>
          )}
        </div>

        {/* Editor Content */}
        <div className={`flex-1 overflow-y-auto p-6 ${isPreviewMode ? 'bg-white' : ''}`}>
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 min-h-[800px]">
              <HoveringToolbar />
              <Editable
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                readOnly={isPreviewMode}
                placeholder="Start typing your report..."
                spellCheck
                autoFocus
                className="outline-none"
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t px-4 py-2 flex items-center justify-between text-sm text-gray-500 flex-shrink-0">
          <span>{editor.children.length} blocks</span>
          <span>Press Ctrl+S to save • Ctrl+B for bold • Ctrl+I for italic</span>
        </div>
      </div>
    </Slate>
  );
}

export default PlateReportEditor;
